import { IOptionItem } from "@/components/ui/dropdown/types";
import { COUNTRY_NAME } from "@/constants/dataset";
import { Timezone } from "@/constants/dateTime";
import { safeBase64Encode } from "@/lib/utils";
import {
  ADMIN_AREA_LEVEL_MAP,
  AdminAreaHierarchy,
  AdminAreaLevelKey,
  EAdminAreaLevel
} from "@/types/adminArea";
import { AnalysisDatasetSchemaItem } from "@/types/analysisSummary";
import { SpatialAggregationType } from "@/types/common";
import {
  AttributeOption,
  DatasetData,
  DatasetDetailResponse,
  DatasetDetailSchemaResponse,
  DatasetFilter,
  DatasetFilterPayload,
  DatasetStatsRequestPayload,
  EAttributeType,
  EFilterOptionType,
  FilterOption,
  H3Configuration
} from "@/types/dataset";
import { AdminAreaSettings } from "@/types/spatial";
import { buildGridConfig } from "@/utils/dataset";
import { splitDoubleColon } from "@/utils/string";

import { buildFilterAsDatasetPayload } from "./filterHelper";
import { removeStartFromUnderscore } from "./string";

export const ADMIN_AREA_SPATIAL_TYPES = [
  "adm_area",
  "poi",
  "H3",
  "polygon",
  "line"
] as const satisfies readonly SpatialAggregationType[];

export const isAdminAreaSpatialType = (
  type: string | undefined | null
): boolean =>
  ADMIN_AREA_SPATIAL_TYPES.includes(
    type as (typeof ADMIN_AREA_SPATIAL_TYPES)[number]
  );

/**
 * Creates an admin area option object from a combined string.
 *
 * This function parses a string in "value::code" format to create an object
 * representing an admin area selection. The label is intentionally left empty
 * as it's expected to be populated later if needed.
 *
 * @param {string} selectedOptString - The string to parse, expected in "value::code" format.
 * @returns {Omit<IOptionItem, "adm_unit"> & { isMatched: boolean }} An object with admin area option properties.
 */
export const createAdminAreaOption = (
  selectedOptString: string
): Omit<IOptionItem, "adm_unit"> & { isMatched: boolean } => {
  const { value, code } = splitDoubleColon(selectedOptString, {
    value: "",
    code: ""
  });
  // Since we don't have the label here, we'll leave it empty.
  // The component using this should be able to fetch it if needed.
  return {
    label: "",
    value,
    code,
    isMatched: true
  };
};

/**
 * Recursively searches an admin area hierarchy tree to find the node whose
 * `full_code` matches the given code.
 *
 * @param {string} code - The `full_code` to search for (e.g. "3205").
 * @param {AdminAreaHierarchy[]} hierarchy - The hierarchy tree to search.
 * @returns {AdminAreaHierarchy | null} The matching node, or `null` if not found.
 */
export const findNodeByCode = (
  code: string,
  hierarchy: AdminAreaHierarchy[]
): AdminAreaHierarchy | null => {
  for (const node of hierarchy) {
    if (node.full_code === code) return node;
    if (node.sub_areas) {
      const found = findNodeByCode(code, node.sub_areas);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Resolves an admin area option string to a UUID.
 *
 * The stored format is `"<UUID>::<full_code>"` (e.g. `"adad131313::1304"`),
 * but recommendation dataset from AI may store only the `full_code` without the UUID
 * (e.g. `"1304"`). In that case, the hierarchy tree is searched to find
 * the corresponding UUID.
 *
 * @param {string} optionString - The option string to resolve.
 * @param {AdminAreaHierarchy[]} [hierarchy] - The hierarchy tree used as a
 *   fallback when no `::` separator is present.
 * @returns {string | null} The resolved UUID, or `null` if it could not be determined.
 */
export const resolveAreaId = (
  optionString: string,
  hierarchy?: AdminAreaHierarchy[]
): string | null => {
  if (optionString.includes("::")) {
    return optionString.split("::")[0];
  }
  if (!hierarchy) return null;
  return findNodeByCode(optionString, hierarchy)?.id ?? null;
};

// `adm_area_level_<n>` — capture the number so any level (e.g. 5) works.
const ADMIN_AREA_LEVEL_PATTERN = /^adm_area_level_(\d+)$/;

/**
 * Whether an attribute code is a filterable admin area level (>= 1).
 * Pattern-based, so new levels work automatically; level 0 (country) excluded.
 * @param attributeCode - The attribute code to test
 * @returns True if it is an admin area level (>= 1)
 */
export const isAdminAreaLevel = (
  attributeCode: string | null | undefined
): boolean => {
  if (!attributeCode) return false;
  const match = ADMIN_AREA_LEVEL_PATTERN.exec(attributeCode);
  return !!match && Number(match[1]) >= 1;
};

/**
 * Helper function to check if an attribute code is NOT an admin area level (1-4)
 * @param attributeCode - The attribute code to test
 * @returns True if the attribute code does NOT match admin area level pattern
 */
export const isNotAdminAreaLevel = (
  attributeCode: string | null | undefined
): boolean => {
  return !isAdminAreaLevel(attributeCode);
};

export const getDatasetAttributeType = (
  dataType: string
): EFilterOptionType | null => {
  switch (dataType) {
    case EAttributeType.ADDRESS:
    case EAttributeType.TITLE:
    case EAttributeType.TEXT:
    case EAttributeType.STRING:
    case EAttributeType.BOOLEAN:
      return EFilterOptionType.MULTIPLE;
    case EAttributeType.DATE_TIME:
      return EFilterOptionType.DATE_TIME;
    case EAttributeType.DATE:
      return EFilterOptionType.DATE;
    case EAttributeType.TIME:
      return EFilterOptionType.TIME;
    case EAttributeType.FLOAT:
    case EAttributeType.INT:
      return EFilterOptionType.MIN_MAX;
    default:
      return null;
  }
};

export const existAttributeOptionsByType = (
  filterOptions: AttributeOption[],
  selectedAttribute: AttributeOption | null,
  filterType: EFilterOptionType
): boolean => {
  if (!selectedAttribute) return false;

  return filterOptions.some(
    (item) => item.id === selectedAttribute.id && item.type === filterType
  );
};

export const generateAttributeFilterOptions = (
  selectedDataset: DatasetDetailResponse | null
): AttributeOption[] => {
  if (!selectedDataset?.schema?.length) return [];

  // Exclude geometry type from the schema. Admin area attributes are kept even
  // when hidden, because the admin area tree needs them to build its filter.
  const schemaExcludeGeomType = selectedDataset.schema?.filter(
    (item) =>
      item.data_type !== "geometry" &&
      (item.is_visible === true || isAdminAreaLevel(item.attribute_code))
  );

  if (!schemaExcludeGeomType.length) return [];

  let filterOptionsMap: AttributeOption[] = schemaExcludeGeomType.map(
    (item) => ({
      ...item,
      id: `${item.id}_${item.position}`,
      label: item.attribute_name,
      type: getDatasetAttributeType(item.data_type),
      time_format: item?.time_format,
      value: item.id
    })
  );

  /* If dataset source is bvt, exclude the Id attribute from the dropdown,
    if source is user dataset, then keep the Id attribute in the dropdown */
  if (selectedDataset.source === "bvt") {
    filterOptionsMap = filterOptionsMap.filter((item) => item.label !== "id");
  }
  return filterOptionsMap;
};

export const mapDatasetFiltersToPayload = (
  filters: DatasetFilter[],
  timezone?: Timezone
) => {
  const uniqueId = new Set<string>();
  const filtersMap: DatasetFilterPayload[] = [];
  filters?.forEach((filter) => {
    const rawAttributeId = filter.paired_key_attribute_id ?? filter.filterId;
    const attributeId = removeStartFromUnderscore(rawAttributeId);
    const attributeCode = filter.attributeCode;
    if (uniqueId.has(attributeId)) {
      const indexOfArray = filtersMap.findIndex(
        (item) => item.id === attributeId
      );
      if (Array.isArray(filtersMap[indexOfArray].filter)) {
        filtersMap[indexOfArray].filter = Array.from(
          new Set([
            ...filtersMap[indexOfArray].filter,
            ...(filter.selectedOptions ?? [])
          ])
        );
        // If there is double identical id, remove attribute_code
        delete filtersMap[indexOfArray].attribute_code;
      }
    } else {
      uniqueId.add(attributeId);
      filtersMap.push({
        id: attributeId,
        filter: buildFilterAsDatasetPayload(filter, timezone),
        ...(attributeCode && attributeCode === "group_name_code"
          ? { attribute_code: attributeCode }
          : {})
      });
    }
  });
  return filtersMap;
};

export function getDeepestAdminFilterFirst(
  filters: DatasetFilter[]
): DatasetFilter[] {
  let highestLevelFilter: DatasetFilter | undefined;
  let highestLevel = -1;
  const otherFilters: DatasetFilter[] = [];

  for (const filter of filters) {
    if (filter.attributeCode?.includes("adm_area_level")) {
      const parts = filter.attributeCode.split("_");
      const level = Number(parts[parts.length - 1]);
      if (!Number.isNaN(level) && level > highestLevel) {
        highestLevel = level;
        highestLevelFilter = filter;
      }
    } else {
      otherFilters.push(filter);
    }
  }

  return highestLevelFilter
    ? [highestLevelFilter, ...otherFilters]
    : otherFilters;
}

export const getAvailAggregationTypeAdminArea = (
  filterOptions: AttributeOption[],
  savedFilters: DatasetFilter[],
  dataset: DatasetDetailResponse | null
) => {
  const filteredSavedFilters = getFilteredSavedFilters(savedFilters, dataset);
  return filterOptions
    .filter((option) => {
      const isAdminArea = isAdminAreaLevel(option.attribute_code);
      const isStringType = option.data_type === "string";
      return !(isAdminArea && isStringType);
    })
    .map((option) => ({
      ...option,
      value: option.id,
      label: option.label,
      disabled: filteredSavedFilters.some(
        (filter: DatasetFilter) =>
          filter.filterId === option.id &&
          filter.attributeCode === option.attribute_code
      )
    }));
};

export const getFilteredSavedFilters = (
  savedFilters: DatasetFilter[],
  dataset: DatasetDetailResponse | null,
  showAdminAreaFilter?: boolean
) => {
  const showAdmin = showAdminAreaFilter ?? false;
  return savedFilters.filter((filter) => {
    const isAdminAreaFilterInSchema = dataset?.schema?.some(
      (schemaItem) =>
        isAdminAreaLevel(schemaItem.attribute_code) &&
        schemaItem.id === filter.attributeId
    );

    return showAdmin ? isAdminAreaFilterInSchema : !isAdminAreaFilterInSchema;
  });
};

export const getValueOfHighestLevelAdminArea = (
  data: AdminAreaSettings | null
): string | null => {
  if (!data) return null;
  // Exclude level 1 because level is country with value like 'id' and not call get admin area from it
  for (let i = 4; i >= 1; i--) {
    const levelKey = `lv${i}` as keyof AdminAreaSettings;
    if (data[levelKey]?.value) return data[levelKey]?.value ?? null;
  }
  return null;
};

/**
 * Helper function to create initial admin area settings based on dataset country
 * @param selectedDataset - The selected dataset
 * @returns Initial admin area settings with lv0 populated if valid country
 */
export const createInitialAdminArea = (
  selectedDataset: DatasetDetailResponse | null
): AdminAreaSettings => {
  const selectedCountry = selectedDataset?.country_id;
  const isValidCountry =
    selectedCountry && selectedCountry !== COUNTRY_NAME.global;

  const lv0Option = isValidCountry
    ? {
        label: COUNTRY_NAME[selectedCountry],
        value: selectedCountry,
        isMatched: false
      }
    : null;

  return {
    lv0: lv0Option,
    lv1: null,
    lv2: null,
    lv3: null,
    lv4: null
  };
};

export interface SelectedAreaSchema {
  level: AdminAreaLevelKey;
  attributeCode: string;
  schema: AttributeOption;
  selectedValue: IOptionItem;
}

/**
 * Helper function to get all selected area schemas from admin area settings
 * @param selectedAdminArea - The selected admin area settings
 * @param filterOptions - Available filter options to match against
 * @returns Array of selected area schemas
 */
export const getAllSelectedAreaSchemas = (
  selectedAdminArea: AdminAreaSettings | null,
  filterOptions: AttributeOption[]
): SelectedAreaSchema[] => {
  if (!selectedAdminArea) return [];

  const levels: readonly AdminAreaLevelKey[] = [
    "lv0",
    "lv1",
    "lv2",
    "lv3",
    "lv4"
  ] as const;
  const schemas: SelectedAreaSchema[] = [];

  for (const level of levels) {
    const selectedValue = selectedAdminArea[level];
    if (selectedValue) {
      const attributeCode = ADMIN_AREA_LEVEL_MAP[level];

      const schemaOption = filterOptions.find(
        (option) => option.attribute_code === attributeCode
      );

      if (schemaOption) {
        schemas.push({
          level,
          attributeCode,
          schema: schemaOption,
          selectedValue
        });
      }
    }
  }

  return schemas;
};

/**
 * Admin area requirement configuration
 */
export interface AdminAreaRequirement {
  lv0: boolean;
  lv1: boolean;
  lv2: boolean;
  lv3: boolean;
  lv4: boolean;
}

/**
 * Admin area visibility configuration
 */
export interface AdminAreaVisibility {
  lv0: boolean;
  lv1: boolean;
  lv2: boolean;
  lv3: boolean;
  lv4: boolean;
}

/**
 * Helper function to determine admin area requirements based on spatial aggregation type
 * @param selectedDataset - The selected dataset
 * @returns Object indicating which admin area levels are required
 */
export const getAdminAreaRequired = (
  selectedDataset: DatasetDetailResponse | null
): AdminAreaRequirement => {
  const defaultRequirement: AdminAreaRequirement = {
    lv0: false,
    lv1: false,
    lv2: false,
    lv3: false,
    lv4: false
  };

  const fallbackCondition =
    !selectedDataset?.schema || selectedDataset?.source !== "bvt";

  // return default requirement when fallback condition is met
  if (fallbackCondition) {
    return defaultRequirement;
  }

  const minAreaLevel = selectedDataset?.min_area_level_filter ?? 0;
  return {
    lv0: minAreaLevel >= 0,
    lv1: minAreaLevel >= 1,
    lv2: minAreaLevel >= 2,
    lv3: minAreaLevel >= 3,
    lv4: minAreaLevel >= 4
  };
};

/**
 * Helper function to determine admin area visibility based on dataset schema
 * @param selectedDataset - The selected dataset
 * @returns Object indicating which admin area levels are visible
 */
export const getAdminAreaVisibility = (
  selectedDataset: DatasetDetailResponse | null
): AdminAreaVisibility => {
  if (!selectedDataset?.schema) {
    return {
      lv0: false,
      lv1: false,
      lv2: false,
      lv3: false,
      lv4: false
    };
  }

  const schemaAttributeCodes = selectedDataset.schema.map(
    (item) => item.attribute_code
  );

  return {
    lv0: true,
    lv1: schemaAttributeCodes.includes(EAdminAreaLevel.LEVEL_1),
    lv2: schemaAttributeCodes.includes(EAdminAreaLevel.LEVEL_2),
    lv3: schemaAttributeCodes.includes(EAdminAreaLevel.LEVEL_3),
    lv4: schemaAttributeCodes.includes(EAdminAreaLevel.LEVEL_4)
  };
};

/**
 * Whether the dataset has ADM area levels in its schema, so the ADM area filter
 * should show. Schema-driven to match the picker (avoids an empty picker).
 * @param selectedDataset - The selected dataset
 * @returns True if the schema has any admin area level (>= 1)
 */
export const hasAdminAreaInSchema = (
  selectedDataset: DatasetDetailResponse | null
): boolean =>
  (selectedDataset?.schema ?? []).some((item) =>
    isAdminAreaLevel(item.attribute_code)
  );

/**
 * Helper function to validate if admin area selection is valid
 * @param selectedAdminArea - The selected admin area settings
 * @param selectedDataset - The selected dataset
 * @returns Boolean indicating if admin area selection is valid
 */
export const isAdminAreaValid = (
  selectedAdminArea: AdminAreaSettings | null,
  selectedDataset: DatasetDetailResponse | null
): boolean => {
  if (!selectedDataset || selectedDataset?.source !== "bvt") {
    return true;
  }

  if (!isAdminAreaSpatialType(selectedDataset?.spatial_aggregation_type)) {
    return true;
  }

  if (!selectedAdminArea) return false;

  const required = getAdminAreaRequired(selectedDataset);

  return Object.entries(required).every(([level, isRequired]) => {
    if (!isRequired) return true;
    const adminAreaKey = level as keyof AdminAreaSettings;
    const adminAreaValue = selectedAdminArea[adminAreaKey];
    return adminAreaValue && adminAreaValue.value;
  });
};

/**
 * Helper function to handle admin area changes and auto-select filter options
 * @param newAdminArea - The new admin area settings
 * @param autoSelectFilter - Whether to auto-select the first available filter
 * @param filterOptions - Available filter options
 * @param savedFilters - Current saved filters
 * @returns Object with selected filter and default value if auto-selection is enabled
 */
export const handleAdminAreaChangeHelper = (
  newAdminArea: AdminAreaSettings | null,
  filterOptions: AttributeOption[],
  savedFilters: DatasetFilter[]
) => {
  const areaSchemas = getAllSelectedAreaSchemas(newAdminArea, filterOptions);

  // Filter out any existing admin area filters from the saved filters.
  const otherFilters = savedFilters.filter(
    (f: DatasetFilter) => !f.attributeCode?.includes("adm_area_level")
  );

  if (areaSchemas.length > 0) {
    const level1Schema = filterOptions.find(
      (opt) => opt.attribute_code === ADMIN_AREA_LEVEL_MAP["lv1"]
    );

    // If there's no schema for level 1, we can't create the filter.
    if (!level1Schema) {
      return otherFilters;
    }

    const allAreaSelectedValueCode = areaSchemas
      .map(
        (areaSchema) =>
          `${areaSchema.selectedValue.value}::${areaSchema.selectedValue.code}`
      )
      .filter(Boolean);

    const adminAreaFilter: DatasetFilter = {
      filterId: level1Schema.id,
      filterLabel: level1Schema.label || "",
      attributeCode: level1Schema.attribute_code || "",
      attributeId: removeStartFromUnderscore(level1Schema.id),
      filterType: EFilterOptionType.MULTIPLE,
      selectedOptions: allAreaSelectedValueCode
    };

    return [adminAreaFilter, ...otherFilters];
  } else {
    // If no admin areas are selected, return only the other non-admin filters.
    return otherFilters;
  }
};

/**
 * Validates a dateTimeRange object to determine if it is suitable for form submission.
 *
 * This function considers the following conditions as valid:
 * - All date and time fields (`from.date`, `from.time`, `to.date`, `to.time`) are empty.
 * - Each of the `from` and `to` objects must either have both `date` and `time` filled in, or both empty.
 *
 * This is typically used to enable or disable a submit button in a form UI.
 *
 * @param {Object} dateTimeRange - The object containing from/to date and time values.
 * @param {Object} [dateTimeRange.from] - The starting date and time range.
 * @param {string} [dateTimeRange.from.date] - The start date string (optional).
 * @param {string} [dateTimeRange.from.time] - The start time string (optional).
 * @param {Object} [dateTimeRange.to] - The ending date and time range.
 * @param {string} [dateTimeRange.to.date] - The end date string (optional).
 * @param {string} [dateTimeRange.to.time] - The end time string (optional).
 *
 * @returns {boolean} `true` if the date-time range is valid for submission (i.e., complete or fully empty), otherwise `false`.
 */
export const isDateTimeRangeValid = (
  dateTimeRange: DatasetFilter["dateTimeRange"]
): boolean => {
  if (!dateTimeRange) return false;

  const isComplete = (part?: { date?: string; time?: string }) =>
    !!part?.date === !!part?.time;

  const isFromComplete = isComplete(dateTimeRange.from);
  const isToComplete = isComplete(dateTimeRange.to);

  const isAllEmpty =
    !dateTimeRange?.from?.date &&
    !dateTimeRange?.from?.time &&
    !dateTimeRange?.to?.date &&
    !dateTimeRange?.to?.time;

  return isAllEmpty || (isFromComplete && isToComplete);
};

export const generateBase64Filters = ({
  filters,
  timezone,
  h3Configuration
}: {
  filters: DatasetFilter[];
  timezone?: Timezone;
  h3Configuration?: H3Configuration | null;
}) => {
  if (!filters.length) return undefined;

  const filterTransformed = getDeepestAdminFilterFirst(filters);
  const filterMap: DatasetFilterPayload[] = mapDatasetFiltersToPayload(
    filterTransformed,
    timezone
  );

  return safeBase64Encode(
    JSON.stringify({
      filters: filterMap,
      ...buildGridConfig(h3Configuration)
    })
  );
};

export const getStreamUrlDatasetStream = ({
  baseUrl,
  filters,
  timezone,
  h3Configuration,
  datasetId,
  show_id,
  analysisId
}: {
  baseUrl: string;
  filters?: DatasetFilter[];
  timezone?: Timezone;
  h3Configuration?: H3Configuration | null;
  datasetId?: string;
  show_id?: boolean;
  analysisId?: string;
}) => {
  if (!datasetId) return "";

  const params = new URLSearchParams();
  const filter = generateBase64Filters({
    filters: filters || [],
    timezone,
    h3Configuration
  });

  if (filter) params.set("filter", filter);
  if (show_id) params.set("show_id", String(show_id));

  const queryString = params.toString() ? `?${params.toString()}` : "";

  if (analysisId) {
    return `${baseUrl}/v1/analysis/${analysisId}/dataset/${datasetId}/stream`;
  } else {
    return `${baseUrl}/v1/datasets/${datasetId}/data/stream${queryString}`;
  }
};

export const mapAnalysisSchemaToDetailSchema = (
  schema: AnalysisDatasetSchemaItem[] = [],
  rawDatasets: Record<string, DatasetData>,
  datasetId: string
): DatasetDetailSchemaResponse[] => {
  const dataset = rawDatasets?.[datasetId];

  // Create a Map for O(1) schema_id lookups
  const columnMap = new Map(
    (dataset?.columns ?? []).map((col) => [col.schema_id, col])
  );

  return schema.map((item) => {
    const timeFormat = columnMap.get(item.id)?.time_format ?? null;

    return {
      ...item,
      data_type: item.data_type ?? "",
      is_nullable: false,
      is_spatial: false,
      is_visible: item?.is_visible ?? false,
      position: 0,
      created_at: "",
      updated_at: "",
      deleted_at: null,
      dataset_id: datasetId,
      paired_key_attribute_id: null,
      time_format: timeFormat
    };
  });
};

/**
 * Retrieves the ID and full code for a list of selected administrative area IDs from the administrative area hierarchy.
 *
 * @param adminAreaTree - The hierarchical structure of administrative areas
 * @param selectedAdminAreaIds - An array of administrative area IDs to retrieve codes for
 * @returns An array of objects containing the ID and full code for each selected administrative area.
 *          Returns an empty array if no matching administrative areas are found.
 *
 * @example
 * const adminAreas = [
 *   { id: '1', full_code: 'ID-12', sub_areas: [...] },
 *   { id: '2', full_code: 'ID-34', sub_areas: [...] }
 * ];
 * const result = getSelectedAdminAreaCodesFromIds(adminAreas, ['1', '2']);
 * // Returns: [{ id: '1', full_code: 'ID-12' }, { id: '2', full_code: 'ID-34' }]
 */
export const getSelectedAdminAreaCodesFromIds = (
  adminAreaTree: AdminAreaHierarchy[],
  selectedAdminAreaIds: string[]
) => {
  const nodeMap = new Map<string, AdminAreaHierarchy>();
  const buildMap = (nodes: AdminAreaHierarchy[]) => {
    for (const node of nodes) {
      nodeMap.set(node.id, node);
      if (node.sub_areas) {
        buildMap(node.sub_areas);
      }
    }
  };
  buildMap(adminAreaTree);

  const highestLevelNodes = selectedAdminAreaIds
    .map((id) => {
      const node = nodeMap.get(id);
      return node ? { id: node.id, full_code: node.full_code } : null;
    })
    .filter((node): node is { id: string; full_code: string } => !!node);

  return highestLevelNodes;
};

/**
 * Creates a dataset filter for administrative areas based on a tree selection.
 *
 * @param {string[]} selectedAdminAreaIds - An array of IDs for the administrative areas selected from the tree.
 * @param {AdminAreaHierarchy[]} adminAreaTree - The complete hierarchical tree structure of administrative areas.
 * @param {AttributeOption[]} filterOptions - The list of available attribute filter options for the dataset.
 * @returns {DatasetFilter | null} A `DatasetFilter` object configured for the selected admin areas, or `null` if the required 'adm_area_level_1' filter option is not found.
 */
export const createAdminAreaTreeFilter = (
  selectedAdminAreaIds: string[],
  adminAreaTree: AdminAreaHierarchy[],
  filterOptions: AttributeOption[]
): DatasetFilter | null => {
  const highestLevelNodes = getSelectedAdminAreaCodesFromIds(
    adminAreaTree,
    selectedAdminAreaIds
  );

  const adminAreaFilterOption = filterOptions.find((opt) =>
    opt.attribute_code?.includes("adm_area_level_1")
  );

  if (!adminAreaFilterOption) return null;

  return {
    filterId: adminAreaFilterOption.id,
    filterLabel: adminAreaFilterOption.label,
    attributeCode: adminAreaFilterOption.attribute_code,
    attributeId: adminAreaFilterOption.value,
    filterType: EFilterOptionType.MULTIPLE,
    paired_key_attribute_id:
      adminAreaFilterOption.paired_key_attribute_id ?? null,
    selectedOptions: highestLevelNodes.map(
      (val) => `${val.id}::${val.full_code}`
    ),
    is_multiple_adm_area: true
  };
};

/**
 * Extracts the defined string `value`s from a list of filter options,
 * dropping any option whose value is undefined or null.
 *
 * @param {FilterOption[]} options - The filter options to read values from
 * @returns {string[]} The defined option values
 */
export const getOptionValues = (options: FilterOption[]): string[] =>
  options
    .map((opt) => opt.value)
    .filter((value): value is string => value !== undefined && value !== null);

/**
 * Calculates the visible selected count by filtering selected options against available options.
 * This is used when admin area changes and previously selected options may no longer exist.
 *
 * @param {string[]} selectedOptions - The array of selected option values
 * @param {FilterOption[]} availableOptions - The array of currently available filter options
 * @returns {number} The count of selected options that exist in available options
 */
export const getVisibleSelectedCount = (
  selectedOptions: string[],
  availableOptions: FilterOption[]
): number => {
  const availableValues = new Set(getOptionValues(availableOptions));
  return selectedOptions.filter((opt) => availableValues.has(opt)).length;
};

/**
 * Builds the request body for the dataset `/stats` endpoint. The `filter`,
 * `grid_config` and `group_by` fields mirror the data-stream payload exactly
 * (same `mapDatasetFiltersToPayload` + `buildGridConfig` outputs), wrapped in
 * the stats-specific `{ request, max_row_count }` envelope.
 */
export const buildDatasetStatsPayload = ({
  filters,
  timezone,
  h3Configuration,
  group_by,
  maxRowCount
}: {
  filters?: DatasetFilter[];
  timezone?: Timezone;
  h3Configuration?: H3Configuration | null;
  group_by?: string;
  maxRowCount: number;
}): DatasetStatsRequestPayload => {
  const filterPayload: DatasetFilterPayload[] = filters?.length
    ? mapDatasetFiltersToPayload(getDeepestAdminFilterFirst(filters), timezone)
    : [];

  return {
    request: {
      filters: filterPayload,
      ...buildGridConfig(h3Configuration),
      ...(group_by && group_by !== "none" ? { group_by } : {}),
      ...(timezone?.code ? { timezone: timezone.code } : {})
    },
    max_row_count: maxRowCount
  };
};
